import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import ConfirmModal from "../../common/ConfirmModal";
import { getInternalActiveStaffLookup } from "../../../services/lookupService";
import { getAvatarColor, getInitials } from "../../../utils/avatarUtils";
import {
  editAppointmentNote,
  hideAppointmentNote,
  saveAppointmentNote,
} from "../eplanner/eplannerService";

function pick(row, ...keys) {
  if (!row) return "";
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return "";
}

function noteType(row) {
  const study = String(pick(row, "StudyName", "studyName") || "").trim();
  if (!study || study.toUpperCase() === "N/A") return "Participant";
  return "Appointment";
}

function noteSortTime(value) {
  const text = String(value || "").trim();
  if (!text) return 0;
  const parsed = Date.parse(text);
  if (!Number.isNaN(parsed)) return parsed;
  const m = text.match(
    /^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)?/i,
  );
  if (!m) return 0;
  const months = {
    jan: 0,
    feb: 1,
    mar: 2,
    apr: 3,
    may: 4,
    jun: 5,
    jul: 6,
    aug: 7,
    sep: 8,
    oct: 9,
    nov: 10,
    dec: 11,
  };
  let hour = Number(m[4]);
  const minute = Number(m[5]);
  const ampm = (m[6] || "").toUpperCase();
  if (ampm === "PM" && hour < 12) hour += 12;
  if (ampm === "AM" && hour === 12) hour = 0;
  const month = months[m[2].toLowerCase()];
  if (month == null) return 0;
  return new Date(Number(m[3]), month, Number(m[1]), hour, minute).getTime();
}

function staffIdOf(staff) {
  return String(
    staff?.StaffId ?? staff?.staffId ?? staff?.StaffIdRef ?? staff?.Value ?? "",
  ).trim();
}

function staffNameOf(staff) {
  const combined = [staff?.FirstName, staff?.LastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return String(
    staff?.StaffName ??
      staff?.staffName ??
      staff?.FullName ??
      staff?.fullName ??
      staff?.Name ??
      staff?.name ??
      staff?.Text ??
      combined ??
      "",
  ).trim();
}

/** Active @query just before caret, e.g. "... hello @aq" → { start, query }. */
function getActiveMention(text, caret) {
  const before = String(text || "").slice(0, Math.max(0, caret));
  const match = before.match(/(^|[\s([{"'])@([^\s@]*)$/);
  if (!match) return null;
  const query = match[2] || "";
  const start = before.length - query.length - 1;
  return { start, query };
}

function NotesPanel({
  notes = [],
  userData,
  volunteerId,
  canEdit = false,
  onChanged,
  showAlert,
  showLoader,
  hideLoader,
}) {
  const noteInputRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null); // { noteId, appointmentId } | null
  const [draft, setDraft] = useState("");
  const [showInAppointments, setShowInAppointments] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [noteMentionStaff, setNoteMentionStaff] = useState([]);
  const [noteMention, setNoteMention] = useState(null);
  const [noteMentionIndex, setNoteMentionIndex] = useState(0);

  const isEditing = Boolean(editingNote?.noteId);

  const rows = useMemo(() => {
    const list = (Array.isArray(notes) ? notes : [])
      .map((row, index) => {
        const noteId = String(
          pick(row, "NotesId", "NoteId", "notesId", "noteId") || "",
        ).trim();
        const type = noteType(row);
        const text = String(pick(row, "Notes", "notes", "Note") || "").trim();
        const when = String(
          pick(row, "UpdatedDateTime", "SystemDateTime", "CreatedDate") || "",
        ).trim();
        const author = String(
          pick(row, "UpdatedBy", "updatedBy", "CreatedBy") || "",
        ).trim();
        return {
          key: noteId || `${type}-${when}-${index}`,
          noteId,
          appointmentId: String(pick(row, "AppId", "AppointmentId") || "").trim(),
          type,
          study: String(pick(row, "StudyName") || "—").trim() || "—",
          visit: String(pick(row, "VisitName") || "—").trim() || "—",
          when: when || "—",
          author,
          text: text || "—",
          sortTime: noteSortTime(when),
        };
      })
      .filter((row) => row.text && row.text !== "—");
    return list.sort((a, b) => b.sortTime - a.sortTime);
  }, [notes]);

  const noteMentionOptions = useMemo(() => {
    if (!noteMention) return [];
    const needle = String(noteMention.query || "")
      .trim()
      .toLowerCase();
    const list = Array.isArray(noteMentionStaff) ? noteMentionStaff : [];
    if (!needle) return list.slice(0, 8);
    return list
      .filter((staff) => staff.StaffName.toLowerCase().includes(needle))
      .slice(0, 8);
  }, [noteMention, noteMentionStaff]);

  useEffect(() => {
    setNoteMentionIndex(0);
  }, [noteMention?.query, noteMentionOptions.length]);

  useEffect(() => {
    if (!modalOpen || !userData?.OrganizationId) {
      setNoteMentionStaff([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const orgStaff = await getInternalActiveStaffLookup(userData, {}).catch(
          () => [],
        );
        if (cancelled) return;
        const byId = new Map();
        (orgStaff || []).forEach((row) => {
          const id = staffIdOf(row);
          const name = staffNameOf(row);
          if (!id || !name) return;
          byId.set(id.toUpperCase(), { StaffId: id, StaffName: name });
        });
        setNoteMentionStaff(
          [...byId.values()].sort((a, b) =>
            a.StaffName.localeCompare(b.StaffName),
          ),
        );
      } catch {
        if (!cancelled) setNoteMentionStaff([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modalOpen, userData]);

  const openAddModal = () => {
    setEditingNote(null);
    setDraft("");
    setShowInAppointments(true);
    setNoteMention(null);
    setNoteMentionIndex(0);
    setModalOpen(true);
  };

  const openEditModal = (row) => {
    if (!row?.noteId) {
      showAlert?.({ message: "This note cannot be edited." });
      return;
    }
    setEditingNote({
      noteId: row.noteId,
      appointmentId: row.appointmentId || "",
    });
    setDraft(row.text === "—" ? "" : row.text);
    setShowInAppointments(true);
    setNoteMention(null);
    setNoteMentionIndex(0);
    setModalOpen(true);
  };

  const closeModal = useCallback(() => {
    if (saving) return;
    setModalOpen(false);
    setEditingNote(null);
    setDraft("");
    setNoteMention(null);
  }, [saving]);

  useEffect(() => {
    if (!modalOpen) return undefined;
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      if (noteMention) {
        setNoteMention(null);
        return;
      }
      closeModal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalOpen, closeModal, noteMention]);

  const updateNoteMentionFromCaret = useCallback((text, caret) => {
    setNoteMention(getActiveMention(text, caret));
  }, []);

  const insertNoteMention = useCallback(
    (staff) => {
      const name = String(staff?.StaffName || "").trim();
      if (!name || !noteMention) return;
      const text = String(draft || "");
      const before = text.slice(0, noteMention.start);
      const replaceEnd =
        noteMention.start + 1 + String(noteMention.query || "").length;
      const after = text.slice(replaceEnd);
      const insertion = `@${name} `;
      const next = `${before}${insertion}${after}`;
      setDraft(next);
      setNoteMention(null);
      requestAnimationFrame(() => {
        const el = noteInputRef.current;
        if (!el) return;
        const pos = before.length + insertion.length;
        el.focus();
        el.setSelectionRange(pos, pos);
      });
    },
    [draft, noteMention],
  );

  const handleNoteDraftChange = useCallback(
    (event) => {
      const value = event.target.value;
      const caret = event.target.selectionStart ?? value.length;
      setDraft(value);
      updateNoteMentionFromCaret(value, caret);
    },
    [updateNoteMentionFromCaret],
  );

  const handleNoteKeyDown = useCallback(
    (event) => {
      if (!noteMention || !noteMentionOptions.length) return;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setNoteMentionIndex((current) =>
          current + 1 >= noteMentionOptions.length ? 0 : current + 1,
        );
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setNoteMentionIndex((current) =>
          current <= 0 ? noteMentionOptions.length - 1 : current - 1,
        );
        return;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        insertNoteMention(
          noteMentionOptions[noteMentionIndex] || noteMentionOptions[0],
        );
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        setNoteMention(null);
      }
    },
    [insertNoteMention, noteMention, noteMentionIndex, noteMentionOptions],
  );

  const handleSave = useCallback(async () => {
    const text = draft.trim();
    if (!text) {
      showAlert?.({ message: "Please enter a note." });
      return;
    }
    if (!volunteerId) return;

    setSaving(true);
    showLoader?.(isEditing ? "Updating note..." : "Saving note...");
    try {
      if (isEditing) {
        await editAppointmentNote({
          userData,
          volunteerId,
          noteId: editingNote.noteId,
          appointmentId: editingNote.appointmentId || "",
          notes: text,
        });
        showAlert?.({ message: "Note updated.", type: "success" });
      } else {
        await saveAppointmentNote({
          userData,
          volunteerId,
          appointmentId: "",
          notes: text,
          noteLevel: "participant",
          showInAppointments,
        });
        showAlert?.({ message: "Note saved.", type: "success" });
      }
      setDraft("");
      setNoteMention(null);
      setEditingNote(null);
      setModalOpen(false);
      await onChanged?.();
    } catch (err) {
      showAlert?.({
        message: err?.message || (isEditing ? "Could not update note." : "Could not save note."),
      });
    } finally {
      hideLoader?.();
      setSaving(false);
    }
  }, [
    draft,
    editingNote,
    hideLoader,
    isEditing,
    onChanged,
    showAlert,
    showInAppointments,
    showLoader,
    userData,
    volunteerId,
  ]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget?.noteId || !volunteerId) return;
    setDeleting(true);
    showLoader?.("Deleting note...");
    try {
      await hideAppointmentNote({
        userData,
        volunteerId,
        noteId: deleteTarget.noteId,
        appointmentId: deleteTarget.appointmentId || "",
      });
      setDeleteTarget(null);
      showAlert?.({ message: "Note deleted.", type: "success" });
      await onChanged?.();
    } catch (err) {
      showAlert?.({ message: err?.message || "Could not delete note." });
    } finally {
      hideLoader?.();
      setDeleting(false);
    }
  }, [
    deleteTarget,
    hideLoader,
    onChanged,
    showAlert,
    showLoader,
    userData,
    volunteerId,
  ]);

  const modal = modalOpen
    ? createPortal(
        <div
          className="pm-notes-modal"
          role="dialog"
          aria-modal="true"
          aria-label={isEditing ? "Edit note" : "Add note"}
        >
          <div className="pm-notes-modal__dialog">
            <header className="pm-notes-modal__header">
              <h3 className="pm-notes-modal__title">
                {isEditing ? "Edit note" : "Add note"}
              </h3>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                disabled={saving}
                onClick={closeModal}
              />
            </header>
            <div className="pm-notes-modal__body">
              <div className="pm-notes-modal__field">
                <label className="pm-notes-modal__label" htmlFor="pm-note-text">
                  Note
                </label>
                <div className="pm-notes-modal__input-wrap">
                  <textarea
                    id="pm-note-text"
                    ref={noteInputRef}
                    className="form-control pm-notes-compose__input"
                    rows={5}
                    value={draft}
                    autoFocus
                    disabled={saving}
                    placeholder="Add a note… type @ to tag staff"
                    onChange={handleNoteDraftChange}
                    onClick={handleNoteDraftChange}
                    onKeyUp={handleNoteDraftChange}
                    onKeyDown={handleNoteKeyDown}
                    onBlur={() => {
                      window.setTimeout(() => setNoteMention(null), 150);
                    }}
                  />
                  {noteMention && noteMentionOptions.length ? (
                    <ul
                      className="pm-notes-modal__mention-list"
                      role="listbox"
                      aria-label="Tag staff"
                    >
                      {noteMentionOptions.map((staff, index) => (
                        <li key={staff.StaffId}>
                          <button
                            type="button"
                            className={[
                              "pm-notes-modal__mention-item",
                              index === noteMentionIndex ? "is-active" : "",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                            onMouseDown={(event) => {
                              event.preventDefault();
                              insertNoteMention(staff);
                            }}
                          >
                            <span
                              className="pm-notes-modal__mention-avatar"
                              style={{
                                background: getAvatarColor(staff.StaffName),
                              }}
                              aria-hidden="true"
                            >
                              {getInitials(staff.StaffName)}
                            </span>
                            <span>{staff.StaffName}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : noteMention && !noteMentionOptions.length ? (
                    <div className="pm-notes-modal__mention-empty">
                      No matching staff
                    </div>
                  ) : null}
                </div>
              </div>

              {!isEditing ? (
                <label className="pm-notes-compose__check">
                  <input
                    type="checkbox"
                    checked={showInAppointments}
                    onChange={(e) => setShowInAppointments(e.target.checked)}
                    disabled={saving}
                  />
                  Show in appointments
                </label>
              ) : null}
            </div>
            <footer className="pm-notes-modal__footer">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={saving}
                onClick={closeModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                disabled={saving || !draft.trim()}
                onClick={handleSave}
              >
                {saving
                  ? isEditing
                    ? "Updating…"
                    : "Saving…"
                  : isEditing
                    ? "Update note"
                    : "Save note"}
              </button>
            </footer>
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div className="pm-notes-tab">
      <div className="pm-notes-tab__head">
        <h3 className="pm-details-section__title mb-0">Notes</h3>
        {canEdit ? (
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={openAddModal}
          >
            <i className="fas fa-plus me-1" aria-hidden="true" />
            Add notes
          </button>
        ) : null}
      </div>

      {rows.length ? (
        <div className="pm-notes-list pm-thin-scroll">
          {rows.map((row) => (
            <article key={row.key} className="pm-note-card">
              <div className="pm-note-card__top">
                <div className="pm-note-card__meta">
                  <span className="pm-doc-card__type">{row.type}</span>
                  <span>{row.when}</span>
                  {row.author ? <span>{row.author}</span> : null}
                  {row.type === "Appointment" ? (
                    <span>
                      {row.study}
                      {row.visit && row.visit !== "—" ? ` · ${row.visit}` : ""}
                    </span>
                  ) : null}
                </div>
                {canEdit && row.noteId ? (
                  <div className="pm-note-card__actions">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary pm-note-card__action"
                      title="Edit note"
                      aria-label="Edit note"
                      onClick={() => openEditModal(row)}
                    >
                      <i className="fas fa-pencil-alt" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger pm-note-card__action"
                      title="Delete note"
                      aria-label="Delete note"
                      onClick={() =>
                        setDeleteTarget({
                          noteId: row.noteId,
                          appointmentId: row.appointmentId,
                          preview: row.text,
                        })
                      }
                    >
                      <i className="fas fa-trash-alt" aria-hidden="true" />
                    </button>
                  </div>
                ) : null}
              </div>
              <p className="pm-note-card__text">{row.text}</p>
            </article>
          ))}
        </div>
      ) : (
        <div className="pm-empty-state">
          No notes yet.
          {canEdit ? " Click Add notes to create one." : ""}
        </div>
      )}

      {modal}

      <ConfirmModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => {
          if (!deleting) setDeleteTarget(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete note"
        message="Are you sure you want to delete this note? It will no longer appear on the participant or appointments."
        confirmText={deleting ? "Deleting…" : "Delete"}
        cancelText="Cancel"
        confirmButtonClass="btn-danger"
        icon="fas fa-trash-alt text-danger"
      />
    </div>
  );
}

export default NotesPanel;
